import { type ReactNode, useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';

export function RequireAuth({ children }: { children: ReactNode }) {
  const auth = useAuth();
  // Guards against re-firing while a signinSilent attempt is in flight: that
  // call sets activeNavigator and isLoading, so this effect re-runs on every
  // state change during the attempt, and without this guard it would start a
  // second silent renewal or jump to signinRedirect before the first settles.
  const attemptedSilent = useRef(false);

  useEffect(() => {
    if (auth.isLoading || auth.isAuthenticated || auth.activeNavigator) return;

    // auth.user survives past expiry (react-oidc-context only clears it on an
    // explicit sign-out), so its presence is what distinguishes "there is a
    // stored session to try renewing" from "never signed in, go straight to
    // the hosted UI." Cognito's refresh token here lives 30 days against a
    // 60-minute id/access token, so almost every cold-open would otherwise
    // force a full re-login despite a perfectly valid refresh token sitting in
    // localStorage. signinSilent() uses that refresh token directly; it only
    // falls back to an iframe if oidc-client-ts finds no refresh_token at all,
    // which isn't configured here, so a refresh-token failure (expired/
    // revoked) is what reaches the catch and falls through to signinRedirect.
    if (auth.user && !attemptedSilent.current) {
      attemptedSilent.current = true;
      auth.signinSilent().catch(() => {
        auth.signinRedirect();
      });
      return;
    }

    auth.signinRedirect();
  }, [auth.isLoading, auth.isAuthenticated, auth.activeNavigator, auth.user, auth]);

  if (auth.isLoading || auth.activeNavigator) {
    return <div className="p-12 text-center text-sm text-muted-foreground">Loading…</div>;
  }
  if (!auth.isAuthenticated) {
    return (
      <div className="p-12 text-center text-sm text-muted-foreground">Redirecting to sign-in…</div>
    );
  }
  return <>{children}</>;
}
